import React, { useState, useEffect, useCallback } from 'react';
import {
  Newspaper,
  Plus,
  Edit2,
  Trash2,
  Power,
  PowerOff,
  Pin,
  PinOff,
  Eye,
  Calendar,
  MapPin,
  Users,
  ExternalLink,
  Search,
  Filter,
  Trophy
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import newsService from '../services/newsService';
import { getAssignedRegions, isRegionScopedAdmin, matchesAssignedRegions } from '../utils/regionAccess';
import ExpandableText from './common/ui/ExpandableText';
import {
  News,
  CreateNewsData,
  NewsStats,
  SportCategory,
  SelectionLevel,
  NewsPriority,
  GenderEligibility,
  SPORT_OPTIONS,
  SELECTION_LEVEL_OPTIONS,
  PRIORITY_OPTIONS
} from '../types/models/news';

const NewsManagement: React.FC = () => {
  const { currentUser, adminProfile } = useAuth();
  const [newsList, setNewsList] = useState<News[]>([]);
  const [stats, setStats] = useState<NewsStats>({ total: 0, active: 0, pinned: 0, upcomingSelections: 0 });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [viewingNews, setViewingNews] = useState<News | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'pinned'>('all');
  const [filterSport, setFilterSport] = useState<SportCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [winnerInput, setWinnerInput] = useState('');
  const [winnerEditMode, setWinnerEditMode] = useState(false);
  const [savingWinner, setSavingWinner] = useState(false);
  const assignedRegions = getAssignedRegions(adminProfile);
  const isRegionScoped = isRegionScopedAdmin(adminProfile);

  // Form state
  const [formData, setFormData] = useState<CreateNewsData>({
    title: '',
    content: '',
    summary: '',
    coverImageUrl: '',
    sport: 'cricket',
    selectionLevel: 'national',
    location: '',
    selectionDate: '',
    endDate: '',
    registrationDeadline: '',
    eligibilityCriteria: '',
    registrationUrl: '',
    contactInfo: '',
    organizingBody: '',
    ageGroup: '',
    gender: 'both',
    maxParticipants: undefined,
    isPinned: false,
    priority: 'normal'
  });

  const loadNews = useCallback(async () => {
    try {
      setLoading(true);
      const newsData = await newsService.getAllNews();
      const scopedNews = newsData.filter((news) => matchesAssignedRegions(adminProfile, news.location));

      const now = new Date();
      const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const scopedStats: NewsStats = {
        total: scopedNews.length,
        active: 0,
        pinned: 0,
        upcomingSelections: 0
      };

      scopedNews.forEach((news) => {
        if (news.isActive) scopedStats.active++;
        if (news.isPinned) scopedStats.pinned++;
        if (news.selectionDate) {
          const selectionDate = new Date(news.selectionDate);
          if (selectionDate >= now && selectionDate <= thirtyDaysLater) {
            scopedStats.upcomingSelections++;
          }
        }
      });

      setNewsList(scopedNews);
      setStats(scopedStats);
    } catch (error) {
      console.error('Error loading news:', error);
      alert('Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [adminProfile]);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const handleOpenCreateModal = () => {
    setEditingNews(null);
    setFormData({
      title: '',
      content: '',
      summary: '',
      coverImageUrl: '',
      sport: 'cricket',
      selectionLevel: 'national',
      location: '',
      selectionDate: '',
      endDate: '',
      registrationDeadline: '',
      eligibilityCriteria: '',
      registrationUrl: '',
      contactInfo: '',
      organizingBody: '',
      ageGroup: '',
      gender: 'both',
      maxParticipants: undefined,
      isPinned: false,
      priority: 'normal'
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (news: News) => {
    setEditingNews(news);
    setFormData({
      title: toTextInputValue(news.title),
      content: toTextInputValue(news.content),
      summary: toTextInputValue(news.summary),
      coverImageUrl: news.coverImageUrl || '',
      sport: news.sport,
      selectionLevel: news.selectionLevel,
      location: toTextInputValue(news.location),
      selectionDate: toDatetimeLocal(news.selectionDate),
      endDate: toDatetimeLocal(news.endDate),
      registrationDeadline: toDateInputValue(news.registrationDeadline),
      eligibilityCriteria: news.eligibilityCriteria || '',
      registrationUrl: news.registrationUrl || '',
      contactInfo: news.contactInfo || '',
      organizingBody: news.organizingBody || '',
      ageGroup: news.ageGroup || '',
      gender: news.gender,
      maxParticipants: news.maxParticipants,
      isPinned: news.isPinned,
      priority: news.priority
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNews(null);
  };

  const handleDeclareWinner = async () => {
    if (!viewingNews) return;
    try {
      setSavingWinner(true);
      await newsService.declareWinner(viewingNews.id, winnerInput);
      setViewingNews({ ...viewingNews, winner: winnerInput.trim() || undefined });
      setWinnerEditMode(false);
      await loadNews();
    } catch {
      alert('Failed to save winner');
    } finally {
      setSavingWinner(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!normalizedFormData.title.trim()) {
      alert('Title is required');
      return;
    }
    if (normalizedFormData.title.length > 150) {
      alert('Title must be 150 characters or less');
      return;
    }
    
    if (normalizedFormData.content.length > 2000) {
      alert('Content must be 2000 characters or less');
      return;
    }
    if (!normalizedFormData.summary.trim()) {
      alert('Summary is required');
      return;
    }
    const summaryWordCount = normalizedFormData.summary.trim() === '' ? 0 : normalizedFormData.summary.trim().split(/\s+/).length;
    if (summaryWordCount > 50) {
      alert('Summary must be 50 words or less');
      return;
    }
    if (!normalizedFormData.location.trim()) {
      alert('Location is required');
      return;
    }
    if (isRegionScoped && !matchesAssignedRegions(adminProfile, normalizedFormData.location)) {
      alert(`News location must match one of your assigned regions: ${assignedRegions.join(', ')}`);
      return;
    }

    try {
      if (editingNews) {
        await newsService.updateNews(editingNews.id, normalizedFormData);
        alert('News updated successfully!');
      } else {
        await newsService.createNews(
          normalizedFormData,
          currentUser?.uid || '',
          currentUser?.displayName || currentUser?.email || 'Admin'
        );
        alert('News created successfully!');
      }

      handleCloseModal();
      loadNews();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Failed to save news');
    }
  };

  const handleDelete = async (news: News) => {
    if (!window.confirm(`Are you sure you want to delete "${news.title}"?`)) {
      return;
    }

    try {
      await newsService.deleteNews(news.id);
      alert('News deleted successfully');
      loadNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Failed to delete news');
    }
  };

  const handleToggleActive = async (news: News) => {
    try {
      await newsService.toggleActive(news.id, !news.isActive);
      loadNews();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update status');
    }
  };

  const handleTogglePinned = async (news: News) => {
    try {
      await newsService.togglePinned(news.id, !news.isPinned);
      loadNews();
    } catch (error) {
      console.error('Error toggling pin:', error);
      alert('Failed to update pin status');
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date}, ${time}`;
  };

  const toDatetimeLocal = (isoString: string | undefined) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const toDateInputValue = (value: unknown) => {
    if (!value) return '';

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      if (trimmed.includes('T')) return trimmed.split('T')[0];

      const parsed = new Date(trimmed);
      return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString().split('T')[0];
    }

    const parsed = new Date(value as string | number | Date);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
  };

  const toTextInputValue = (value: unknown) => (
    typeof value === 'string' ? value : value == null ? '' : String(value)
  );

  const normalizedFormData: CreateNewsData = {
    ...formData,
    title: toTextInputValue(formData.title),
    content: toTextInputValue(formData.content),
    summary: toTextInputValue(formData.summary),
    coverImageUrl: toTextInputValue(formData.coverImageUrl),
    location: toTextInputValue(formData.location),
    selectionDate: toTextInputValue(formData.selectionDate),
    endDate: toTextInputValue(formData.endDate),
    registrationDeadline: toTextInputValue(formData.registrationDeadline),
    eligibilityCriteria: toTextInputValue(formData.eligibilityCriteria),
    registrationUrl: toTextInputValue(formData.registrationUrl),
    contactInfo: toTextInputValue(formData.contactInfo),
    organizingBody: toTextInputValue(formData.organizingBody),
    ageGroup: toTextInputValue(formData.ageGroup)
  };

  const getStatusBadge = (news: News) => {
    if (!news.isActive) {
      return <span className="badge badge-gray">Inactive</span>;
    }
    if (news.isPinned) {
      return <span className="badge badge-blue">Pinned</span>;
    }
    return <span className="badge badge-green">Active</span>;
  };

  const getPriorityBadge = (priority: NewsPriority) => {
    const classes: Record<NewsPriority, string> = {
      low: 'badge-gray',
      normal: 'badge-blue',
      high: 'badge-yellow',
      urgent: 'badge-red'
    };
    return <span className={`badge ${classes[priority]}`}>{priority.toUpperCase()}</span>;
  };

  const getLevelBadge = (level: SelectionLevel | null | undefined) => {
    if (!level) {
      return <span className="badge badge-gray">N/A</span>;
    }
    const classes: Record<SelectionLevel, string> = {
      national: 'badge-purple',
      state: 'badge-blue',
      district: 'badge-green',
      zonal: 'badge-yellow',
      university: 'badge-gray'
    };
    return <span className={`badge ${classes[level]}`}>{level.charAt(0).toUpperCase() + level.slice(1)}</span>;
  };

  // Filter news
  const filteredNews = newsList.filter(news => {
    // Status filter
    if (filterStatus === 'active' && !news.isActive) return false;
    if (filterStatus === 'pinned' && !news.isPinned) return false;

    // Sport filter
    if (filterSport !== 'all' && news.sport !== filterSport) return false;

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        toTextInputValue(news.title).toLowerCase().includes(query) ||
        toTextInputValue(news.summary).toLowerCase().includes(query) ||
        toTextInputValue(news.location).toLowerCase().includes(query) ||
        toTextInputValue(news.organizingBody).toLowerCase().includes(query)
      );
    }

    return true;
  });

  return (
    <div className="news-management">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <Newspaper size={32} />
            Selection News Management
          </h1>
          <p className="page-subtitle">Create and manage selection news for athletes (visible to all users)</p>
          {isRegionScoped && (
            <p className="page-subtitle" style={{ marginTop: '8px', color: '#92400e' }}>
              Region scope active: {assignedRegions.length > 0 ? assignedRegions.join(', ') : 'No regions assigned'}
            </p>
          )}
        </div>
        <button className="btn btn-primary" onClick={handleOpenCreateModal}>
          <Plus size={20} />
          Create News
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total News</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-label">Active</div>
          <div className="stat-value">{stats.active}</div>
        </div>
        <div className="stat-card stat-info">
          <div className="stat-label">Pinned</div>
          <div className="stat-value">{stats.pinned}</div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-label">Upcoming Selections</div>
          <div className="stat-value">{stats.upcomingSelections}</div>
          <div className="stat-note">Next 30 days</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search news..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'pinned')}
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="pinned">Pinned Only</option>
          </select>

          <select
            value={filterSport}
            onChange={(e) => setFilterSport(e.target.value as SportCategory | 'all')}
          >
            <option value="all">All Sports</option>
            {SPORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* News Table */}
      {loading ? (
        <div className="loading-state">Loading news...</div>
      ) : filteredNews.length === 0 ? (
        <div className="empty-state">
          <Newspaper size={48} />
          <h3>No news found</h3>
          <p>Create your first selection news to get started</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Title</th>
                <th>Sport</th>
                <th>Level</th>
                <th>Location</th>
                <th>Selection Date</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNews.map(news => (
                <tr key={news.id}>
                  <td>{getStatusBadge(news)}</td>
                  <td>
                    <div className="news-title-cell">
                      <span className="font-semibold">{news.title}</span>
                      <span className="news-summary">{news.summary.substring(0, 50)}...</span>
                    </div>
                  </td>
                  <td className="capitalize">{news.sport}</td>
                  <td>{getLevelBadge(news.selectionLevel)}</td>
                  <td>{news.location}</td>
                  <td>{formatDate(news.selectionDate)}</td>
                  <td>{news.views}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-icon-info"
                        onClick={() => { setViewingNews(news); setWinnerInput(news.winner || ''); setWinnerEditMode(false); }}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="btn-icon btn-icon-primary"
                        onClick={() => handleOpenEditModal(news)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className={`btn-icon ${news.isPinned ? 'btn-icon-warning' : 'btn-icon-default'}`}
                        onClick={() => handleTogglePinned(news)}
                        title={news.isPinned ? 'Unpin' : 'Pin'}
                      >
                        {news.isPinned ? <PinOff size={16} /> : <Pin size={16} />}
                      </button>
                      <button
                        className={`btn-icon ${news.isActive ? 'btn-icon-warning' : 'btn-icon-success'}`}
                        onClick={() => handleToggleActive(news)}
                        title={news.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {news.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                      </button>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDelete(news)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingNews ? 'Edit News' : 'Create Selection News'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Title */}
                <div className="form-group">
                  <label htmlFor="title">
                    Title <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="form-input"
                    value={normalizedFormData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    maxLength={150}
                    placeholder="e.g., National Cricket Selection Trials 2025"
                    required
                  />
                  <div className="char-counter">{normalizedFormData.title.length} / 150</div>
                </div>

                {/* Summary */}
                <div className="form-group">
                  <label htmlFor="summary">
                    Summary <span className="required">*</span>
                  </label>
                  <textarea
                    id="summary"
                    className="form-textarea"
                    value={normalizedFormData.summary}
                    onChange={e => {
                      const words = e.target.value.trim() === '' ? [] : e.target.value.trim().split(/\s+/);
                      if (words.length <= 50) {
                        setFormData({ ...formData, summary: e.target.value });
                      }
                    }}
                    rows={2}
                    placeholder="Brief summary for preview (max 50 words)"
                    required
                  />
                  <div className="char-counter">
                    {normalizedFormData.summary.trim() === '' ? 0 : normalizedFormData.summary.trim().split(/\s+/).length} / 50 words
                  </div>
                </div>

                

                {/* Content */}
                <div className="form-group">
                  <label htmlFor="content">Content</label>
                  <textarea
                    id="content"
                    className="form-textarea"
                    value={normalizedFormData.content}
                    onChange={e => {
                      if (e.target.value.length <= 2000) {
                        setFormData({ ...formData, content: e.target.value });
                      }
                    }}
                    rows={5}
                    maxLength={2000}
                    placeholder="Full article content (max 2000 characters)"
                  />
                  <div className="char-counter">{normalizedFormData.content.length} / 2000</div>
                </div>

                {/* Sport & Level */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sport">Sport <span className="required">*</span></label>
                    <select
                      id="sport"
                      className="form-input"
                      value={formData.sport}
                      onChange={e => setFormData({ ...formData, sport: e.target.value as SportCategory })}
                      required
                    >
                      {SPORT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="selectionLevel">Selection Level <span className="required">*</span></label>
                    <select
                      id="selectionLevel"
                      className="form-input"
                      value={formData.selectionLevel}
                      onChange={e => setFormData({ ...formData, selectionLevel: e.target.value as SelectionLevel })}
                      required
                    >
                      {SELECTION_LEVEL_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Location */}
                <div className="form-group">
                  <label htmlFor="location">
                    <MapPin size={16} />
                    Location <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    className="form-input"
                    value={normalizedFormData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., National Cricket Academy, Bangalore"
                    required
                  />
                </div>

                {/* Dates */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="selectionDate">
                      <Calendar size={16} />
                      Start Date &amp; Time
                    </label>
                    <input
                      type="datetime-local"
                      id="selectionDate"
                      className="form-input"
                      value={normalizedFormData.selectionDate}
                      onChange={e => setFormData({ ...formData, selectionDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="endDate">
                      <Calendar size={16} />
                      End Date &amp; Time
                    </label>
                    <input
                      type="datetime-local"
                      id="endDate"
                      className="form-input"
                      value={normalizedFormData.endDate}
                      onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="registrationDeadline">
                      <Calendar size={16} />
                      Registration Deadline
                    </label>
                    <input
                      type="date"
                      id="registrationDeadline"
                      className="form-input"
                      value={normalizedFormData.registrationDeadline}
                      onChange={e => setFormData({ ...formData, registrationDeadline: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="maxParticipants">
                      <Users size={16} />
                      Max Participants
                    </label>
                    <input
                      type="number"
                      id="maxParticipants"
                      className="form-input"
                      value={formData.maxParticipants ?? ''}
                      min={1}
                      onChange={e => setFormData({ ...formData, maxParticipants: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                      placeholder="e.g., 100"
                    />
                  </div>
                </div>

                {/* Age Group & Gender */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ageGroup">Age Group</label>
                    <input
                      type="text"
                      id="ageGroup"
                      className="form-input"
                      value={normalizedFormData.ageGroup}
                      onChange={e => setFormData({ ...formData, ageGroup: e.target.value })}
                      placeholder="e.g., Under-19, Senior, 18-25 years"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gender">
                      <Users size={16} />
                      Gender Eligibility
                    </label>
                    <select
                      id="gender"
                      className="form-input"
                      value={formData.gender}
                      onChange={e => setFormData({ ...formData, gender: e.target.value as GenderEligibility })}
                    >
                      <option value="both">Both (Male & Female)</option>
                      <option value="male">Male Only</option>
                      <option value="female">Female Only</option>
                    </select>
                  </div>
                </div>

                {/* Organizing Body */}
                <div className="form-group">
                  <label htmlFor="organizingBody">Organizing Body</label>
                  <input
                    type="text"
                    id="organizingBody"
                    className="form-input"
                    value={normalizedFormData.organizingBody}
                    onChange={e => setFormData({ ...formData, organizingBody: e.target.value })}
                    placeholder="e.g., BCCI, SAI, State Sports Association"
                  />
                </div>

                {/* Eligibility Criteria */}
                <div className="form-group">
                  <label htmlFor="eligibilityCriteria">Eligibility Criteria</label>
                  <textarea
                    id="eligibilityCriteria"
                    className="form-textarea"
                    value={normalizedFormData.eligibilityCriteria}
                    onChange={e => setFormData({ ...formData, eligibilityCriteria: e.target.value })}
                    maxLength={500}
                    rows={3}
                    placeholder="Who can apply? Age requirements, experience level, etc."
                  />
                  <div className="char-counter">{normalizedFormData.eligibilityCriteria?.length || 0} / 500</div>
                </div>

                {/* URLs */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="registrationUrl">
                      <ExternalLink size={16} />
                      Source URL
                    </label>
                    <input
                      type="url"
                      id="registrationUrl"
                      className="form-input"
                      value={normalizedFormData.registrationUrl}
                      onChange={e => setFormData({ ...formData, registrationUrl: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="coverImageUrl">Cover Image URL</label>
                    <input
                      type="url"
                      id="coverImageUrl"
                      className="form-input"
                      value={normalizedFormData.coverImageUrl}
                      onChange={e => setFormData({ ...formData, coverImageUrl: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* Contact Info */}
                <div className="form-group">
                  <label htmlFor="contactInfo">Contact Information</label>
                  <input
                    type="text"
                    id="contactInfo"
                    className="form-input"
                    value={normalizedFormData.contactInfo}
                    onChange={e => setFormData({ ...formData, contactInfo: e.target.value })}
                    placeholder="Phone, email, or other contact details"
                  />
                </div>

                {/* Priority & Pin */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="priority">Priority</label>
                    <select
                      id="priority"
                      className="form-input"
                      value={formData.priority}
                      onChange={e => setFormData({ ...formData, priority: e.target.value as NewsPriority })}
                    >
                      {PRIORITY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isPinned}
                        onChange={e => setFormData({ ...formData, isPinned: e.target.checked })}
                      />
                      <Pin size={16} />
                      Pin to top of news feed
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingNews ? 'Update News' : 'Create News'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingNews && (
        <div className="modal-overlay" onClick={() => setViewingNews(null)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>News Details</h2>
              <button className="modal-close" onClick={() => setViewingNews(null)}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="view-header">
                <h3>{viewingNews.title}</h3>
                <div className="view-badges">
                  {getStatusBadge(viewingNews)}
                  {getLevelBadge(viewingNews.selectionLevel)}
                  {getPriorityBadge(viewingNews.priority)}
                </div>
              </div>

              {viewingNews.coverImageUrl && (
                <img src={viewingNews.coverImageUrl} alt={viewingNews.title} className="view-cover-image" />
              )}

              {/* Event Details — structured summary */}
              <div className="event-details-block">
                <div className="event-detail-row">
                  <span className="event-detail-label">
                    <Calendar size={15} />
                    START DATE &amp; TIME
                  </span>
                  <span className="event-detail-value">{formatDateTime(viewingNews.selectionDate)}</span>
                </div>
                <div className="event-detail-row">
                  <span className="event-detail-label">
                    <Calendar size={15} />
                    END DATE &amp; TIME
                  </span>
                  <span className="event-detail-value">{formatDateTime(viewingNews.endDate)}</span>
                </div>
                <div className="event-detail-row">
                  <span className="event-detail-label">
                    <Calendar size={15} />
                    REGISTRATION DEADLINE
                  </span>
                  <span className="event-detail-value">{formatDate(viewingNews.registrationDeadline)}</span>
                </div>
                <div className="event-detail-row">
                  <span className="event-detail-label">
                    <MapPin size={15} />
                    LOCATION
                  </span>
                  <span className="event-detail-value">{viewingNews.location || '-'}</span>
                </div>
                <div className="event-detail-row">
                  <span className="event-detail-label">
                    <Users size={15} />
                    PARTICIPANTS
                  </span>
                  <span className="event-detail-value">
                    {viewingNews.maxParticipants ? `Max ${viewingNews.maxParticipants}` : '-'}
                    {viewingNews.ageGroup ? ` · ${viewingNews.ageGroup}` : ''}
                    {viewingNews.gender !== 'both' ? ` · ${viewingNews.gender === 'male' ? 'Male Only' : 'Female Only'}` : ''}
                  </span>
                </div>

                {/* Declare Winner */}
                <div className="event-detail-winner">
                  <div className="event-detail-winner-header">
                    <span className="event-detail-label" style={{ width: 'auto' }}>
                      <Trophy size={15} />
                      WINNER
                    </span>
                    {!winnerEditMode && (
                      <button
                        className="winner-declare-btn"
                        onClick={() => { setWinnerInput(viewingNews.winner || ''); setWinnerEditMode(true); }}
                      >
                        {viewingNews.winner ? 'Edit Winner' : 'Declare Winner'}
                      </button>
                    )}
                  </div>

                  {winnerEditMode ? (
                    <div className="winner-input-row">
                      <input
                        type="text"
                        className="form-input winner-input"
                        value={winnerInput}
                        onChange={e => setWinnerInput(e.target.value)}
                        placeholder="Enter winner name(s)"
                        autoFocus
                      />
                      <button
                        className="btn btn-primary"
                        onClick={handleDeclareWinner}
                        disabled={savingWinner}
                      >
                        {savingWinner ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setWinnerEditMode(false)}
                        disabled={savingWinner}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span className="event-detail-value winner-value">
                      {viewingNews.winner
                        ? <><Trophy size={14} className="trophy-icon" />{viewingNews.winner}</>
                        : <span className="no-winner">Not declared yet</span>}
                    </span>
                  )}
                </div>
              </div>

              <div className="view-grid">
                <div className="view-item">
                  <span className="view-label">Sport</span>
                  <span className="view-value capitalize">{viewingNews.sport}</span>
                </div>
                <div className="view-item">
                  <span className="view-label">Organizing Body</span>
                  <span className="view-value">{viewingNews.organizingBody || '-'}</span>
                </div>
                <div className="view-item">
                  <span className="view-label">Gender</span>
                  <span className="view-value capitalize">{viewingNews.gender}</span>
                </div>
                <div className="view-item">
                  <span className="view-label">Views</span>
                  <span className="view-value">{viewingNews.views}</span>
                </div>
              </div>

              <div className="view-section">
                <h4>Summary</h4>
                <ExpandableText text={viewingNews.summary} limit={150} className="text-gray-900" />
              </div>

              <div className="view-section">
                <h4>Full Content</h4>
                <ExpandableText text={viewingNews.content} limit={300} className="view-content text-gray-900" />
              </div>

              {viewingNews.eligibilityCriteria && (
                <div className="view-section">
                  <h4>Eligibility Criteria</h4>
                  <p>{viewingNews.eligibilityCriteria}</p>
                </div>
              )}

              {viewingNews.contactInfo && (
                <div className="view-section">
                  <h4>Contact Information</h4>
                  <p>{viewingNews.contactInfo}</p>
                </div>
              )}

              {viewingNews.registrationUrl && (
                <div className="view-section">
                  <a href={viewingNews.registrationUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                    <ExternalLink size={16} />
                    Open Source URL
                  </a>
                </div>
              )}

              <div className="view-meta">
                <span>Created by: {viewingNews.authorName}</span>
                <span>Created: {formatDate(viewingNews.createdAt)}</span>
                <span>Updated: {formatDate(viewingNews.updatedAt)}</span>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setViewingNews(null)}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => {
                setViewingNews(null);
                handleOpenEditModal(viewingNews);
              }}>
                <Edit2 size={16} />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .news-management {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
        }

        .page-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .page-subtitle {
          color: #666;
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-label {
          font-size: 14px;
          color: #666;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #333;
        }

        .stat-note {
          font-size: 12px;
          color: #888;
          margin-top: 4px;
        }

        .stat-success .stat-value { color: #22c55e; }
        .stat-info .stat-value { color: #3b82f6; }
        .stat-warning .stat-value { color: #f59e0b; }

        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          gap: 16px;
          flex-wrap: wrap;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 8px 12px;
          flex: 1;
          max-width: 300px;
        }

        .search-box input {
          border: none;
          outline: none;
          flex: 1;
          font-size: 14px;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-group select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          background: white;
        }

        .table-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: #f9fafb;
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }

        .data-table tr:hover {
          background: #f9fafb;
        }

        .news-title-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .news-summary {
          font-size: 12px;
          color: #666;
        }

        .capitalize {
          text-transform: capitalize;
        }

        .badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .badge-green { background: #dcfce7; color: #166534; }
        .badge-yellow { background: #fef3c7; color: #92400e; }
        .badge-red { background: #fee2e2; color: #991b1b; }
        .badge-gray { background: #f3f4f6; color: #6b7280; }
        .badge-blue { background: #dbeafe; color: #1e40af; }
        .badge-purple { background: #f3e8ff; color: #7c3aed; }

        .action-buttons {
          display: flex;
          gap: 6px;
        }

        .btn-icon {
          padding: 6px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-icon-primary { background: #dbeafe; color: #1e40af; }
        .btn-icon-primary:hover { background: #bfdbfe; }
        .btn-icon-success { background: #dcfce7; color: #166534; }
        .btn-icon-success:hover { background: #bbf7d0; }
        .btn-icon-warning { background: #fef3c7; color: #92400e; }
        .btn-icon-warning:hover { background: #fde68a; }
        .btn-icon-danger { background: #fee2e2; color: #991b1b; }
        .btn-icon-danger:hover { background: #fecaca; }
        .btn-icon-info { background: #e0e7ff; color: #4338ca; }
        .btn-icon-info:hover { background: #c7d2fe; }
        .btn-icon-default { background: #f3f4f6; color: #374151; }
        .btn-icon-default:hover { background: #e5e7eb; }

        .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary { background: #3b82f6; color: white; }
        .btn-primary:hover { background: #2563eb; }
        .btn-secondary { background: #f3f4f6; color: #374151; }
        .btn-secondary:hover { background: #e5e7eb; }

        .loading-state, .empty-state {
          text-align: center;
          padding: 48px;
          background: white;
          border-radius: 8px;
        }

        .empty-state svg { color: #d1d5db; margin-bottom: 16px; }
        .empty-state h3 { margin: 0 0 8px 0; color: #374151; }
        .empty-state p { color: #6b7280; margin: 0; }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-large {
          max-width: 800px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }

        .modal-header h2 { margin: 0; font-size: 20px; }

        .modal-close {
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }

        .modal-close:hover { background: #f3f4f6; }

        .modal-body { padding: 24px; }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          position: sticky;
          bottom: 0;
          background: white;
        }

        .form-group { margin-bottom: 16px; }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #374151;
        }

        .required { color: #ef4444; }

        .form-input, .form-textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          box-sizing: border-box;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-textarea {
          resize: vertical;
          font-family: inherit;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .char-counter {
          font-size: 12px;
          color: #6b7280;
          text-align: right;
          margin-top: 4px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding-top: 28px;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
        }

        .view-header {
          margin-bottom: 16px;
        }

        .view-header h3 {
          margin: 0 0 8px 0;
          font-size: 24px;
        }

        .view-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .view-cover-image {
          width: 100%;
          max-height: 300px;
          object-fit: cover;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .view-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .view-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .view-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
        }

        .view-value {
          font-weight: 600;
          color: #374151;
        }

        .view-section {
          margin-bottom: 16px;
        }

        .view-section h4 {
          margin: 0 0 8px 0;
          color: #374151;
        }

        .view-section p {
          margin: 0;
          color: #4b5563;
          line-height: 1.6;
        }

        .view-content {
          white-space: pre-wrap;
        }

        .view-meta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          color: #6b7280;
        }

        @media (max-width: 768px) {
          .news-management { padding: 16px; }
          .page-header { flex-direction: column; gap: 16px; }
          .stats-grid { grid-template-columns: 1fr 1fr; }
          .form-row { grid-template-columns: 1fr; }
          .filters-section { flex-direction: column; align-items: stretch; }
          .search-box { max-width: none; }
          .filter-group { flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
};

export default NewsManagement;
